/* eslint-disable max-len */
import { ErrorMessage, Formik, useFormikContext } from 'formik';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import FileUploadStatus from '../../../../components/FileUploadStatus/FileUploadStatus';
import FormDropDown from '../../../../components/FormInputs/FormDropdown/FormDropdown';
import Panel from '../../../../components/Panel/Panel';
import { useConfirmationModalContext } from '../../../../hooks/useConfirmationModalContext';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  selectDyslexiaScreenerFiles,
  selectDyslexiaScreenerPanelNewStaffTrainingRequired,
} from '../../../../redux/slices/dyslexiaScreenerSlice';
import {
  deleteDyslexiaScreenerFileThunk,
  fetchDyslexiaScreenerFilesBySchoolThunk,
  uploadDyslexiaScreenerFileThunk,
} from '../../../../redux/thunks/dyslexiaScreenerThunks';
import { downloadDyslexiaScreenerFile } from '../../../../services/dyslexiaScreener.service';
import {
  getMaxFileUploadSizeMB,
  getMaxUploadFilenameLength,
} from '../../../../services/fileUpload.service';
import {
  DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_OPTIONS,
  getDyslexiaScreenerDocumentUploadLabel,
} from '../../../../types/DyslexiaScreenerDocumentUploadType';
import { DyslexiaScreenerFileListItemModel } from '../../../../types/DyslexiaScreenerFileListItemModel';
import { DyslexiaScreenerModel } from '../../../../types/DyslexiaScreenerModel';
import { PanelType } from '../../../../types/PanelType';
import {
  DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_ERROR_OTHER_TRAINING_PERMISSION,
  DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_ERROR_TRAINING_PARTICIPANTS,
} from '../../../../types/propTypes/dyslexiaScreenerDocumentUploadValidationMessages';
import type { SelectOption } from '../../../../types/SelectOption';
import yup from '../../../../types/Validation/CustomValidation/yup-extended';
import {
  dyslexiaScreenerDocumentUploadRequirementFlags,
  dyslexiaScreenerPendingFileUploadRef,
  dyslexiaScreenerUploadedFilesSatisfyDocumentRequirements,
  DYSLEXIA_SCREENER_TRAINING_SOURCE_OTHER_VALUE,
} from '../../../../utilities/dyslexiaScreenerUtilities';
import { createDownloadLinkAndExecute } from '../../../../utilities/formUtilities';
import {
  isDistrictUser,
  isStateAdmin,
} from '../../../../utilities/userUtilities';
import './DyslexiaScreenerUploadDocuments.css';

interface UploadedDocument {
  id: string;
  documentName: string;
  documentTypeLabel: string;
  uploadedBy: string;
  uploadDate: string;
}

interface FileUploadState {
  progress: number;
  isCompleted: boolean;
  isFailed: boolean;
  error?: string;
}

interface DyslexiaScreenerUploadDocumentsProps {
  /** Calendar year for file API (e.g. "2026"), same as Seal of Biliteracy upload. */
  schoolYear?: string;
  leaIdentifier?: string;
  schoolIdentifier?: string;
  dyslexiaScreenerId?: string;
  disabled?: boolean;
}

function getLastSegmentFromFilename(filename: string): string {
  const parts = filename.split(';');
  return parts[parts.length - 1];
}

const DOC_UPLOAD_TRAINING_ATTENDANCE =
  DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_OPTIONS[0].value;
const DOC_UPLOAD_OTHER_PERMISSION =
  DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_OPTIONS[1].value;
const DOC_UPLOAD_SUPPORTING =
  DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_OPTIONS[2].value;

const isDocumentUploadTypeRequiringFileSelection = (
  documentType: string | undefined | null
): boolean =>
  documentType === DOC_UPLOAD_TRAINING_ATTENDANCE ||
  documentType === DOC_UPLOAD_OTHER_PERMISSION;

const getDocumentUploadRequiredMessageForType = (
  documentType: string | undefined | null
): string => {
  if (
    !documentType?.trim() ||
    documentType === DOC_UPLOAD_SUPPORTING ||
    !isDocumentUploadTypeRequiringFileSelection(documentType)
  ) {
    return '';
  }
  if (documentType === DOC_UPLOAD_TRAINING_ATTENDANCE) {
    return DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_ERROR_TRAINING_PARTICIPANTS;
  }
  if (documentType === DOC_UPLOAD_OTHER_PERMISSION) {
    return DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_ERROR_OTHER_TRAINING_PERMISSION;
  }
  return '';
};

const documentUploadTypeFormNoopSubmit = (): void => {
  void 0;
};

interface DocumentUploadTypeDropdownProps {
  selectedValue: string;
  disabled: boolean;
  onCommittedChange: (value: string) => void;
  documentTypeLabelClasses: string;
  selectOptions: SelectOption[];
  documentTypeConditionalRequired: boolean;
}

const DocumentUploadTypeDropdown = ({
  selectedValue,
  disabled,
  onCommittedChange,
  documentTypeLabelClasses,
  selectOptions,
  documentTypeConditionalRequired,
}: DocumentUploadTypeDropdownProps): ReactElement => {
  const documentUploadTypeValidationSchema = useMemo(
    () =>
      yup.object({
        documentUploadType: documentTypeConditionalRequired
          ? yup.string().trim().required('Field is required.')
          : yup.string(),
      }),
    [documentTypeConditionalRequired]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={{ documentUploadType: selectedValue ?? '' }}
      validationSchema={documentUploadTypeValidationSchema}
      onSubmit={documentUploadTypeFormNoopSubmit}
    >
      <div className="document-upload-type-row">
        <FormDropDown
          displayName="Document Type"
          field="documentUploadType"
          options={selectOptions}
          disabled={disabled}
          isClearable={!disabled}
          placeholder="Select..."
          classConfig={{
            containerClasses: 'dyslexia-screener-upload-document-type-dropdown',
            labelClasses: documentTypeLabelClasses,
          }}
          data-testid="dyslexia-screener-document-upload-type"
          onChangeEvent={async ({ value }) => {
            if (value === undefined || value === null) {
              onCommittedChange('');
              return;
            }
            if (typeof value === 'string' && value.trim().length > 0) {
              onCommittedChange(value);
            }
          }}
        />
      </div>
    </Formik>
  );
};

const DyslexiaScreenerUploadDocuments = ({
  schoolYear,
  leaIdentifier,
  schoolIdentifier,
  dyslexiaScreenerId,
  disabled = false,
}: DyslexiaScreenerUploadDocumentsProps): ReactElement => {
  const {
    values,
    validateForm,
    setFieldError,
    setFieldTouched,
    setErrors,
    errors,
  } = useFormikContext<DyslexiaScreenerModel>();
  const dispatch = useAppDispatch();
  const { error: reduxError } = useAppSelector(
    (state) => state.dyslexiaScreener
  );
  const filesToUse = useAppSelector(selectDyslexiaScreenerFiles);
  const panelNewStaffTrainingRequired = useAppSelector(
    selectDyslexiaScreenerPanelNewStaffTrainingRequired
  );
  const { showConfirmation, setOptions } = useConfirmationModalContext();
  const [selectedDocumentUploadType, setSelectedDocumentUploadType] =
    useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [fileChooserOpened, setFileChooserOpened] = useState(false);
  const fileChooserOpenedRef = useRef(false);
  const selectedFileRef = useRef<File | null>(null);
  const selectedDocumentUploadTypeRef = useRef(selectedDocumentUploadType);
  const [uploadState, setUploadState] = useState<FileUploadState>({
    progress: 0,
    isCompleted: false,
    isFailed: false,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    selectedFileRef.current = selectedFile;
  }, [selectedFile]);

  useEffect(() => {
    selectedDocumentUploadTypeRef.current = selectedDocumentUploadType;
  }, [selectedDocumentUploadType]);

  useEffect(() => {
    setSelectedDocumentUploadType('');
    setSelectedFile(null);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [dyslexiaScreenerId]);

  const canUpload = Boolean(dyslexiaScreenerId?.trim());

  const isReadOnlyUser = (): boolean => {
    if (isStateAdmin() || isDistrictUser()) return false;
    return true;
  };

  const trainingAcknowledgementDateSelected = Boolean(
    (values.trainingAcknowledgementDate ?? '').trim()
  );

  const uploadControlsDisabled =
    disabled ||
    isReadOnlyUser() ||
    !canUpload ||
    !trainingAcknowledgementDateSelected;

  const fileChooserDisabled =
    uploadControlsDisabled || !selectedDocumentUploadType?.trim();

  const {
    documentUploadSelectOptions,
    documentTypeLabelClasses,
    documentTypeConditionalRequired,
    showDocumentUploadRequiredLabel,
  } = useMemo(() => {
    void panelNewStaffTrainingRequired;

    const flags = dyslexiaScreenerDocumentUploadRequirementFlags({
      schoolYear: Number(values.schoolYear),
      trainingAcknowledgementDate: values.trainingAcknowledgementDate ?? '',
      trainingUsed: values.trainingUsed ?? '',
    });

    const { missingTrainingParticipants, missingOtherPermission } =
      dyslexiaScreenerUploadedFilesSatisfyDocumentRequirements(
        filesToUse,
        flags
      );

    const isSupportingDocumentUploadTypeSelected =
      selectedDocumentUploadType === DOC_UPLOAD_SUPPORTING;

    const showDocumentTypeRequiredAsterisk =
      !isSupportingDocumentUploadTypeSelected &&
      (missingOtherPermission || missingTrainingParticipants);

    const showDocumentUploadRequiredLabelValue =
      Boolean(selectedDocumentUploadType?.trim()) &&
      showDocumentTypeRequiredAsterisk;

    const trainingUsedTrimmed = (values.trainingUsed ?? '').trim();
    const trainingSourceIsOther =
      trainingUsedTrimmed === DYSLEXIA_SCREENER_TRAINING_SOURCE_OTHER_VALUE;

    const uploadTypeSelectOptions: SelectOption[] =
      DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_OPTIONS.map(({ value, label }) => ({
        value,
        text: label,
        isDisabled:
          trainingSourceIsOther && value === DOC_UPLOAD_TRAINING_ATTENDANCE,
      }));

    return {
      documentUploadSelectOptions: uploadTypeSelectOptions,
      documentTypeLabelClasses: showDocumentTypeRequiredAsterisk
        ? 'forced-required-label'
        : '',
      documentTypeConditionalRequired: showDocumentTypeRequiredAsterisk,
      showDocumentUploadRequiredLabel: showDocumentUploadRequiredLabelValue,
    };
  }, [
    filesToUse,
    panelNewStaffTrainingRequired,
    selectedDocumentUploadType,
    values.schoolYear,
    values.trainingAcknowledgementDate,
    values.trainingUsed,
  ]);

  const hasUnfinishedUpload = Boolean(
    selectedFile && !uploadState.isFailed
  );

  const shouldKeepDocumentUploadFieldValidation = useMemo(
    () =>
      Boolean(selectedDocumentUploadType?.trim()) &&
      isDocumentUploadTypeRequiringFileSelection(selectedDocumentUploadType) &&
      showDocumentUploadRequiredLabel,
    [selectedDocumentUploadType, showDocumentUploadRequiredLabel]
  );

  const resetDocumentUploadFieldValidation = (): void => {
    setFileError('');
    setFieldError('dyslexiaScreenerDocumentUpload', undefined);
    void setFieldTouched('dyslexiaScreenerDocumentUpload', false, false);
  };

  useEffect(() => {
    dyslexiaScreenerPendingFileUploadRef.hasPendingFile = hasUnfinishedUpload;
    void validateForm();
  }, [hasUnfinishedUpload, validateForm]);

  useEffect(() => {
    return () => {
      dyslexiaScreenerPendingFileUploadRef.hasPendingFile = false;
    };
  }, []);

  useEffect(() => {
    void validateForm().then(() => {
      if (shouldKeepDocumentUploadFieldValidation) {
        return;
      }
      resetDocumentUploadFieldValidation();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    validateForm,
    filesToUse,
    panelNewStaffTrainingRequired,
    values.schoolYear,
    values.trainingAcknowledgementDate,
    values.trainingUsed,
    values.id,
    hasUnfinishedUpload,
    selectedDocumentUploadType,
    showDocumentUploadRequiredLabel,
    documentTypeConditionalRequired,
    shouldKeepDocumentUploadFieldValidation,
  ]);

  useEffect(() => {
    if (shouldKeepDocumentUploadFieldValidation) {
      return;
    }
    resetDocumentUploadFieldValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldKeepDocumentUploadFieldValidation]);

  const uploadedDocuments: UploadedDocument[] = filesToUse.map((file) => ({
    id: file.id,
    documentName: getLastSegmentFromFilename(file.fileName),
    documentTypeLabel: getDyslexiaScreenerDocumentUploadLabel(
      file.documentType
    ),
    uploadedBy: file.uploadedBy || 'Unknown',
    uploadDate: file.uploadedDate ? file.uploadedDate.split(' ')[0] : 'Unknown',
  }));

  const validateFile = (file: File): string => {
    const allowedExtensions = ['.pdf'];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf('.'));

    if (!allowedExtensions.includes(fileExtension)) {
      return 'Invalid file type, must be .pdf';
    }

    const maxSizeMB: number = getMaxFileUploadSizeMB();
    const maxSize = maxSizeMB * 1024 * 1024;

    if (file.size > maxSize) {
      return 'Files cannot be larger than 50 MB';
    }

    const maxFilenameLength = getMaxUploadFilenameLength();
    if (file.name.length > maxFilenameLength) {
      return 'Filename exceeds max length of 50 characters';
    }

    return '';
  };

  const clearDocumentUploadFieldError = (): void => {
    const newErrors = { ...(errors ?? {}) } as Record<string, unknown>;
    delete newErrors.dyslexiaScreenerDocumentUpload;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setErrors(newErrors as any);
  };

  const applyRequiredDocumentSelectionError = (
    documentType: string | undefined | null = selectedDocumentUploadType
  ): void => {
    const message = getDocumentUploadRequiredMessageForType(documentType);
    if (!message) {
      return;
    }
    setFileError(message);
    setFieldError('dyslexiaScreenerDocumentUpload', message);
    void setFieldTouched('dyslexiaScreenerDocumentUpload', true, false);
  };

  useEffect((): (() => void) => {
    const fileInput = fileInputRef.current;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    if (!fileInput) return (): void => {};

    const handleInputChange = (event: Event): void => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (file) {
        const error = validateFile(file);
        setFileError(error);
        setSelectedFile(file);
        clearDocumentUploadFieldError();
      }
    };

    const handleInputError = (): void => {
      setFileError(
        'Error selecting file. The file might be too large or corrupted.'
      );
      setSelectedFile(null);
    };

    fileInput.addEventListener('change', handleInputChange);
    fileInput.addEventListener('error', handleInputError);

    return () => {
      fileInput.removeEventListener('change', handleInputChange);
      fileInput.removeEventListener('error', handleInputError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFieldError]);

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0];

    if (file) {
      clearDocumentUploadFieldError();
      if (file.size === 0) {
        setFileError(
          'File appears to be empty or corrupted. Please try a different file.'
        );
        setSelectedFile(file);
        return;
      }

      const error = validateFile(file);
      setFileError(error);
      setSelectedFile(file);
    } else if (
      isDocumentUploadTypeRequiringFileSelection(selectedDocumentUploadType)
    ) {
      setSelectedFile(null);
      applyRequiredDocumentSelectionError(selectedDocumentUploadType);
    } else {
      setFileError(
        'File is too large for the browser to handle. Please try a smaller file.'
      );
      setSelectedFile(null);
    }
  };

  useEffect(() => {
    if (!fileChooserOpened) {
      return;
    }

    const handleWindowFocus = (): void => {
      if (!fileChooserOpenedRef.current) {
        return;
      }
      fileChooserOpenedRef.current = false;
      setFileChooserOpened(false);

      window.setTimeout(() => {
        if (selectedFileRef.current) {
          return;
        }
        if (
          !isDocumentUploadTypeRequiringFileSelection(
            selectedDocumentUploadTypeRef.current
          )
        ) {
          return;
        }
        applyRequiredDocumentSelectionError(
          selectedDocumentUploadTypeRef.current
        );
      }, 0);
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileChooserOpened, setFieldError, setFieldTouched]);

  const handleChooseFiles = (): void => {
    if (fileInputRef.current) {
      setFileError('');
      clearDocumentUploadFieldError();
      fileChooserOpenedRef.current = true;
      setFileChooserOpened(true);
      fileInputRef.current.click();
    }
  };

  const handleClearFile = (): void => {
    setSelectedFile(null);
    setFileError('');
    clearDocumentUploadFieldError();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const prevTrainingUsedRef = useRef((values.trainingUsed ?? '').trim());

  useEffect(() => {
    const currentTrainingUsed = (values.trainingUsed ?? '').trim();
    if (prevTrainingUsedRef.current === currentTrainingUsed) {
      return;
    }
    prevTrainingUsedRef.current = currentTrainingUsed;
    setSelectedDocumentUploadType('');
    setSelectedFile(null);
    resetDocumentUploadFieldValidation();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, values.trainingUsed]);

  const handleTryAgain = (): void => {
    setSelectedFile(null);
    setFileError('');
    clearDocumentUploadFieldError();
    setUploadState({
      progress: 0,
      isCompleted: false,
      isFailed: false,
    });
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadFile = async (): Promise<void> => {
    if (!selectedFile) {
      applyRequiredDocumentSelectionError();
      return;
    }

    const error = validateFile(selectedFile);
    if (error) {
      setFileError(error);
      return;
    }

    if (!schoolIdentifier?.trim()) {
      setUploadState({
        progress: 0,
        isCompleted: false,
        isFailed: true,
        error: 'School identifier is required for file upload.',
      });
      return;
    }

    if (!leaIdentifier?.trim()) {
      setUploadState({
        progress: 0,
        isCompleted: false,
        isFailed: true,
        error: 'LEA identifier is required for file upload.',
      });
      return;
    }

    if (!schoolYear?.trim()) {
      setUploadState({
        progress: 0,
        isCompleted: false,
        isFailed: true,
        error:
          'School year is required for file upload (calendar year, e.g. 2026).',
      });
      return;
    }

    if (!canUpload) {
      setUploadState({
        progress: 0,
        isCompleted: false,
        isFailed: true,
        error: 'Save this dyslexia screener record before uploading documents.',
      });
      return;
    }

    if (!selectedDocumentUploadType?.trim()) {
      setUploadState({
        progress: 0,
        isCompleted: false,
        isFailed: true,
        error: 'Select a document type before uploading.',
      });
      return;
    }

    setIsUploading(true);
    setUploadState({
      progress: 0,
      isCompleted: false,
      isFailed: false,
    });

    const progressInterval = setInterval(() => {
      setUploadState((prevState) => {
        if (prevState.progress < 0.9) {
          return {
            ...prevState,
            progress: Math.min(prevState.progress + 0.1, 0.9),
          };
        }
        return prevState;
      });
    }, 200);

    try {
      const result = await dispatch(
        uploadDyslexiaScreenerFileThunk({
          schoolYear,
          schoolIdentifier: schoolIdentifier.trim(),
          leaIdentifier: leaIdentifier.trim(),
          file: selectedFile,
          documentType: selectedDocumentUploadType,
        })
      );

      clearInterval(progressInterval);

      if (uploadDyslexiaScreenerFileThunk.fulfilled.match(result)) {
        setUploadState({
          progress: 1,
          isCompleted: true,
          isFailed: false,
        });

        if (schoolYear && leaIdentifier && schoolIdentifier) {
          try {
            const fetchResult = await dispatch(
              fetchDyslexiaScreenerFilesBySchoolThunk({
                schoolYear,
                leaId: leaIdentifier,
                schoolIdentifier,
                showLoading: false,
              })
            );
            if (
              !fetchDyslexiaScreenerFilesBySchoolThunk.fulfilled.match(
                fetchResult
              )
            ) {
              console.warn(
                'Refetch dyslexia screener files after upload did not succeed'
              );
            }
          } catch (fetchError) {
            console.error(
              'Error fetching updated files after upload:',
              fetchError
            );
          }
        }

        setTimeout(() => {
          handleClearFile();
          setSelectedDocumentUploadType('');
          setUploadState({
            progress: 0,
            isCompleted: false,
            isFailed: false,
          });
        }, 1500);
      } else if (uploadDyslexiaScreenerFileThunk.rejected.match(result)) {
        let errorMessage =
          result.error?.message ||
          reduxError ||
          'Failed to upload file. Please try again.';

        if (
          errorMessage.includes('500') ||
          errorMessage.includes('Internal Server Error')
        ) {
          errorMessage =
            'The server encountered an error while processing your file. Please try again.';
        } else if (
          errorMessage.includes('400') ||
          errorMessage.includes('Bad Request')
        ) {
          errorMessage =
            'The file could not be processed. Please check the file format and try again.';
        } else if (
          errorMessage.includes('413') ||
          errorMessage.includes('Request Entity Too Large')
        ) {
          errorMessage = 'The file is too large. Please choose a smaller file.';
        } else if (
          errorMessage.includes('timeout') ||
          errorMessage.includes('Timeout')
        ) {
          errorMessage = 'The upload timed out. Please try again.';
        }

        setUploadState({
          progress: 0,
          isCompleted: false,
          isFailed: true,
          error: errorMessage,
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (uploadError) {
      console.error('Upload error:', uploadError);
      let errorMessage =
        uploadError instanceof Error
          ? uploadError.message
          : reduxError || 'Failed to upload file. Please try again.';

      if (
        errorMessage.includes('500') ||
        errorMessage.includes('Internal Server Error')
      ) {
        errorMessage =
          'The server encountered an error while processing your file. Please try again.';
      } else if (
        errorMessage.includes('400') ||
        errorMessage.includes('Bad Request')
      ) {
        errorMessage =
          'The file could not be processed. Please check the file format and try again.';
      } else if (
        errorMessage.includes('413') ||
        errorMessage.includes('Request Entity Too Large')
      ) {
        errorMessage = 'The file is too large. Please choose a smaller file.';
      } else if (
        errorMessage.includes('timeout') ||
        errorMessage.includes('Timeout')
      ) {
        errorMessage = 'The upload timed out. Please try again.';
      }

      setUploadState({
        progress: 0,
        isCompleted: false,
        isFailed: true,
        error: errorMessage,
      });
    } finally {
      clearInterval(progressInterval);
      setIsUploading(false);
    }
  };

  const handleDownloadDocument = async (
    fileDocument: DyslexiaScreenerFileListItemModel
  ): Promise<void> => {
    if (!leaIdentifier) {
      console.error('LEA identifier is required to download files.');
      return;
    }

    try {
      const blob = await downloadDyslexiaScreenerFile(
        leaIdentifier,
        fileDocument.id
      );
      createDownloadLinkAndExecute(fileDocument.fileName, blob);
    } catch (downloadErr) {
      console.error('Download error:', downloadErr);
    }
  };

  const handleDeleteDocument = async (documentId: string): Promise<void> => {
    if (!leaIdentifier) {
      setDeleteError('LEA identifier is required to delete files.');
      return;
    }

    const documentToDelete = filesToUse.find((file) => file.id === documentId);
    const documentName = documentToDelete?.fileName || 'this document';

    setOptions({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete "${getLastSegmentFromFilename(
        documentName
      )}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeleteError('');
        try {
          setIsDeleting(documentId);

          const result = await dispatch(
            deleteDyslexiaScreenerFileThunk({
              leaId: leaIdentifier,
              id: documentId,
            })
          );

          if (deleteDyslexiaScreenerFileThunk.fulfilled.match(result)) {
            if (schoolYear && leaIdentifier && schoolIdentifier) {
              try {
                const fetchResult = await dispatch(
                  fetchDyslexiaScreenerFilesBySchoolThunk({
                    schoolYear,
                    leaId: leaIdentifier,
                    schoolIdentifier,
                    showLoading: false,
                  })
                );
                if (
                  !fetchDyslexiaScreenerFilesBySchoolThunk.fulfilled.match(
                    fetchResult
                  )
                ) {
                  console.warn(
                    'Refetch dyslexia screener files after delete did not succeed'
                  );
                }
              } catch (refetchErr) {
                console.error(
                  'Error refetching files after delete:',
                  refetchErr
                );
              }
            }
          } else if (deleteDyslexiaScreenerFileThunk.rejected.match(result)) {
            setDeleteError(
              result.error?.message ||
                'Failed to delete file. Please try again.'
            );
          }

          return {
            status: 200,
            data: null,
            error: { type: '', title: '', status: 200, detail: '' },
            aborted: false,
          };
        } catch (err) {
          setDeleteError(
            err instanceof Error
              ? err.message
              : 'Failed to delete file. Please try again.'
          );
          return {
            status: 500,
            data: null,
            error: { type: '', title: '', status: 500, detail: '' },
            aborted: false,
          };
        } finally {
          setIsDeleting(null);
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onCancel: () => {},
    });

    await showConfirmation();
  };

  return (
    <div className="dyslexia-screener-upload-documents">
      <Panel panelType={PanelType.INFO} heading="Upload Documents">
        <div className="upload-documents-content">
          <ErrorMessage
            name="dyslexiaScreenerDocumentUploadRequirement"
            render={(msg): ReactElement | null => {
              const text = typeof msg === 'string' ? msg : '';
              if (!text) {
                return null;
              }
              return (
                <div
                  className="document-upload-requirement-error"
                  role="alert"
                  data-testid="dyslexia-screener-document-upload-requirement-error"
                >
                  {text}
                </div>
              );
            }}
          />
          <div className="file-upload-section">
            <div className="document-type-and-file-upload-row">
              <div className="document-type-and-file-upload-row__type">
                <DocumentUploadTypeDropdown
                  key={dyslexiaScreenerId ?? 'new'}
                  selectedValue={selectedDocumentUploadType}
                  disabled={uploadControlsDisabled}
                  documentTypeLabelClasses={documentTypeLabelClasses}
                  documentTypeConditionalRequired={
                    documentTypeConditionalRequired
                  }
                  selectOptions={documentUploadSelectOptions}
                  onCommittedChange={(value) => {
                    setSelectedDocumentUploadType(value);
                    if (
                      !value.trim() ||
                      !isDocumentUploadTypeRequiringFileSelection(value)
                    ) {
                      resetDocumentUploadFieldValidation();
                    }
                  }}
                />
              </div>
              <div className="document-type-and-file-upload-row__file">
                {!isUploading && !selectedFile && (
                  <div className="file-input-container">
                    <label
                      className={`field-label ${documentTypeLabelClasses}`.trim()}
                      htmlFor="dyslexia-screener-document-file-input"
                      data-testid="dyslexia-screener-document-upload-label"
                      style={{
                        visibility: showDocumentUploadRequiredLabel
                          ? 'visible'
                          : 'hidden',
                      }}
                      aria-hidden={!showDocumentUploadRequiredLabel}
                    >
                      Document Upload Required
                    </label>
                    <div className="file-input-row">
                      <div
                        className={`file-input-wrapper ${
                          fileError ? 'file-input-wrapper--error' : ''
                        }`}
                      >
                        <input
                          ref={fileInputRef}
                          id="dyslexia-screener-document-file-input"
                          type="file"
                          accept=".pdf"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                          data-testid="dyslexia-screener-file-input"
                          multiple={false}
                          disabled={fileChooserDisabled}
                        />
                        <button
                          type="button"
                          className="choose-files-button"
                          onClick={handleChooseFiles}
                          data-testid="dyslexia-screener-choose-files-button"
                          disabled={fileChooserDisabled}
                        >
                          Choose File
                        </button>
                        <span className="no-file-chosen">No file chosen</span>
                      </div>
                      <div className="file-requirements">
                        <div>
                          PDF files only. {getMaxFileUploadSizeMB()} MB max size
                          per file.
                        </div>
                        <div>
                          {getMaxUploadFilenameLength()} characters maximum
                          filename length
                        </div>
                      </div>
                    </div>
                    {fileError ? (
                      <div
                        className="document-upload-field-error"
                        role="alert"
                        data-testid="dyslexia-screener-file-selection-error"
                      >
                        {fileError}
                      </div>
                    ) : null}
                  </div>
                )}

                {!isUploading && selectedFile && !uploadState.isFailed && (
                  <div className="file-selected-container">
                    <div
                      className={`file-selected-item ${
                        fileError ? 'file-selected-item--error' : ''
                      }${
                        fileError ? ' file-selected-item--has-inline-error' : ''
                      }`}
                    >
                      <span className="file-name">{selectedFile.name}</span>
                      {fileError ? (
                        <span
                          className="file-error file-error--inline"
                          data-testid="dyslexia-screener-file-error"
                        >
                          {fileError}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        className="delete-file-button"
                        onClick={handleClearFile}
                        data-testid="dyslexia-screener-delete-file-button"
                        aria-label="Delete file"
                      >
                        <img src="/images/icon-trash.svg" alt="Delete" />
                      </button>
                    </div>
                    <div className="file-actions">
                      <ActionButton
                        onClick={handleClearFile}
                        classes="button no-wrap-text cancel-button"
                        dataTestId="dyslexia-screener-clear-file-button"
                        disabled={disabled || isUploading}
                      >
                        Clear File
                      </ActionButton>
                      <ActionButton
                        onClick={handleUploadFile}
                        classes="button--secondary submit-button"
                        dataTestId="dyslexia-screener-upload-file-button"
                        disabled={
                          disabled ||
                          isUploading ||
                          !!fileError ||
                          !selectedDocumentUploadType?.trim()
                        }
                      >
                        Upload File
                      </ActionButton>
                    </div>
                  </div>
                )}

                {!isUploading && selectedFile && uploadState.isFailed && (
                  <div className="file-selected-container">
                    <div className="file-selected-item file-selected-item--error file-selected-item--has-inline-error">
                      <span className="file-name">{selectedFile.name}</span>
                      {uploadState.error ? (
                        <span
                          className="file-error file-error--inline"
                          data-testid="dyslexia-screener-file-error"
                        >
                          {uploadState.error}
                        </span>
                      ) : null}
                    </div>
                    <div className="file-actions">
                      <ActionButton
                        onClick={handleTryAgain}
                        classes="button no-wrap-text cancel-button"
                        dataTestId="dyslexia-screener-try-again-button"
                        disabled={disabled || isUploading}
                      >
                        Try again
                      </ActionButton>
                    </div>
                  </div>
                )}

                {isUploading && selectedFile && (
                  <div className="file-upload-progress">
                    <FileUploadStatus
                      fileType={selectedFile.name}
                      percent={uploadState.progress}
                      isCompleted={uploadState.isCompleted}
                      isFailed={uploadState.isFailed}
                      error={uploadState.error}
                    />
                    <div className="progress-ruler"></div>
                  </div>
                )}

                {!isUploading && uploadState.isFailed && !selectedFile && (
                  <div className="file-selected-container">
                    <div className="file-selected-item file-selected-item--error file-selected-item--has-inline-error">
                      <span className="file-name">Upload Failed</span>
                      {uploadState.error ? (
                        <span
                          className="file-error file-error--inline"
                          data-testid="dyslexia-screener-file-error"
                        >
                          {uploadState.error}
                        </span>
                      ) : null}
                    </div>
                    <div className="file-actions">
                      <ActionButton
                        onClick={handleTryAgain}
                        classes="button no-wrap-text cancel-button"
                        dataTestId="dyslexia-screener-try-again-button"
                        disabled={disabled || isUploading}
                      >
                        Try again
                      </ActionButton>
                    </div>
                  </div>
                )}
                <ErrorMessage
                  name="dyslexiaScreenerDocumentUpload"
                  render={(msg): ReactElement | null => {
                    const text = typeof msg === 'string' ? msg : '';
                    if (!text || (!selectedFile && fileError)) {
                      return null;
                    }
                    return (
                      <div
                        className="document-upload-field-error"
                        role="alert"
                        data-testid="dyslexia-screener-document-must-be-uploaded-error"
                      >
                        {text}
                      </div>
                    );
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="uploaded-documents-section">
          <h3 className="table-title">List of Uploaded documents</h3>
          {deleteError && (
            <div
              className="delete-error"
              data-testid="dyslexia-screener-delete-error"
            >
              {deleteError}
            </div>
          )}
          <div className="uploaded-documents-table-container">
            <table
              className="uploaded-documents-table"
              data-testid="dyslexia-screener-uploaded-documents-table"
            >
              <thead>
                <tr>
                  <th>Document Name</th>
                  <th>Document Type</th>
                  <th>Uploaded By</th>
                  <th>Uploaded Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {uploadedDocuments.length > 0 ? (
                  uploadedDocuments.map((doc) => {
                    const originalFile = filesToUse.find(
                      (file) => file.id === doc.id
                    );
                    return (
                      <tr
                        key={doc.id}
                        data-testid={`dyslexia-screener-document-row-${doc.id}`}
                      >
                        <td>
                          <button
                            type="button"
                            className="document-name-link"
                            onClick={() =>
                              originalFile &&
                              handleDownloadDocument(originalFile)
                            }
                            data-testid={`dyslexia-screener-download-document-${doc.id}`}
                            aria-label={`Download ${doc.documentName}`}
                            disabled={disabled}
                          >
                            {doc.documentName}
                          </button>
                        </td>
                        <td>{doc.documentTypeLabel}</td>
                        <td>{doc.uploadedBy}</td>
                        <td>{doc.uploadDate}</td>
                        <td>
                          <button
                            type="button"
                            className="delete-document-button"
                            onClick={() => handleDeleteDocument(doc.id)}
                            disabled={
                              disabled ||
                              isDeleting === doc.id ||
                              isReadOnlyUser() ||
                              !canUpload
                            }
                            data-testid={`dyslexia-screener-delete-document-${doc.id}`}
                            aria-label={`Delete ${doc.documentName}`}
                          >
                            {isDeleting === doc.id ? (
                              <div className="delete-loading-spinner">
                                <div></div>
                                <div></div>
                                <div></div>
                                <div></div>
                              </div>
                            ) : (
                              <img src="/images/icon-trash.svg" alt="Delete" />
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="no-documents"
                      data-testid="no-documents"
                    >
                      No documents uploaded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default DyslexiaScreenerUploadDocuments;
